import { Maybe } from '@codegrade/maybe';
/**
 * Check if a user is a member of a given group.
 *
 * @param self The user to check for.
 * @param group The group to check in.
 * @returns If the user is a member of the group.
 */
export function isMemberOf(self, group) {
    return group.members.some(member => member.id === self.id);
}
/**
 * Check if two users are the same, or if one is a member of the other (if it's a group).
 *
 * @param self The first user.
 * @param other The second user.
 * @returns If the users are equal or one is a member of the other.
 */
export function isEqualOrMemberOf(self, other) {
    if (other.id === self.id) {
        return true;
    }
    else if (other.type === 'group-user') {
        return self.type === 'normal' && isMemberOf(self, other.group);
    }
    else if (self.type === 'group-user' && other.type === 'normal') {
        return isMemberOf(other, self.group);
    }
    return false;
}
/**
 * Get the anonymization method to use, if any.
 *
 * @param root0.user The user to potentially anonymize.
 * @param root0.loggedIn The currently logged-in user.
 * @param root0.assignment The assignment for which you might need to anonymize.
 * @param root0.group The group context, if any.
 * @returns Maybe a method to get the anonymized name of the user with.
 */
function anonymizationMethod({ user, loggedIn, assignment, group }) {
    // Never anonymize the test student, early fast bailout.
    if (assignment.isNothing() || (user.type === 'normal' && user.isTestStudent)) {
        return undefined;
    }
    const method = assignment.value.anonymizationMethod.extract();
    if (method == null) {
        return undefined;
    }
    // Never anonymize the logged in user
    if (loggedIn.isNothing()) {
        return method;
    }
    if (isEqualOrMemberOf(user, loggedIn.value)) {
        return undefined;
    }
    // Do not anonymize members of the group of the logged in user.
    if (group.isJust() &&
        loggedIn.value.type === 'normal' &&
        isMemberOf(loggedIn.value, group.value)) {
        return undefined;
    }
    return method;
}
/**
 * Should we anonymize for the given assignment.
 *
 * @param opts The options for display name generation.
 * @returns If the name of this user should be anonymized.
 */
export function shouldAnonymize(opts) {
    return anonymizationMethod(opts) != null;
}
/**
 * Get the name we should display for this user.
 *
 * @param opts The options for display name generation.
 * @returns The name of the user as it should be displayed, or `Nothing` if the name is removed
 * from our system.
 */
export function displayName(opts) {
    const user = opts.user;
    const method = anonymizationMethod(opts);
    if (user.type === 'group-user') {
        const groupName = method?.(user.id) ?? user.group.name;
        if (opts.prefixGroup === true) {
            return Maybe.of(`Group "${groupName}"`);
        }
        else {
            return Maybe.of(groupName);
        }
    }
    else {
        if (method == null) {
            return user.name;
        }
        return Maybe.of(method(user.id));
    }
}
/**
 * Get the list of normal users that are represented by a user.
 * If the user is a group, it returns the members. If it is a normal user, it returns the user itself in a list.
 *
 * @param user The user to get the contained users from.
 * @returns A readonly array of all contained normal users.
 */
export function containedUsers(user) {
    if (user.type === 'group-user') {
        return user.group.members;
    }
    return [user];
}
/**
 * A generator that yields all users contained within a given user, including the user itself if it's a group.
 *
 * @param user The user to get the contained users from.
 * @yields The contained normal users, and then the group user itself if applicable.
 */
export function* containedUsersWithGroup(user) {
    if (user.type === 'group-user') {
        yield* user.group.members;
    }
    yield user;
}
/**
 * Get the set of ids of all contained users.
 *
 * @param user The user to get the contained user IDs from.
 * @returns The set of ids of all contained users.
 */
export function containedUserIds(user) {
    if (user.type === 'group-user') {
        return new Set(user.group.members.map(member => member.id));
    }
    return new Set([user.id]);
}
